import { Button, Checkbox, DialogActions, DialogContent, DialogTitle, Grid, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { renderClose, useDialog } from "../../common/dialog";
import { UseEntityStore, createEntityStore } from "../../common/entityStore";
import { propBoolField, propDateField, propNumberField, propTextField } from "../../common/entityStoreForm";
import { propRequired, propLengthMax, propValidValue, propValueValidator } from "../../common/entityStoreValidators";
import { CallCenterDto, CallCenterProjectDto } from "./types";
import { useCallCenters } from "../../common/enum";
import { UseListStore, createListStore } from "../../common/listStore";
import { EntityStoreSaveButton } from "../../common/SaveButton";
import { ExpandLess, ExpandMore, MoreVert } from "@mui/icons-material";
import { formatDate } from "../../common/localization";
import { LandCode, useDefaultValues } from "../../common/defaultValues";

export function CallCenterDialog(props: {
    close: () => void;
    callCenter: CallCenterDto | null;
    onChange(): void;
    useListStore: UseListStore<CallCenterDto>;
}) {
    const [useStore] = useState(() => createEntityStore<CallCenterDto>({
        data: props.callCenter ?? { isActive: true, projects: {}, name: "", hideCherrypickData: false, codForUnconfirmed: false, apiTokens: "" },
        path: "settings/callCenters",
        fetchOnInit: false,
        id: props.callCenter?.id ?? null,
        getNewId: p => p.id!,
        onSaved() {
            props.onChange();
            props.close();
            useCallCenters.getState().uninit();
        },
        propValidators: [
            propRequired("name"),
            propLengthMax("name", 100),
        ],
    }));

    const [useProjectInfosStore] = useState(() => createListStore<CallCenterProjectInfoDto>({
        path: "settings/callCenters/projects",
    }));
    const projectInfos = useProjectInfosStore(p => p.items);

    const { id, name } = useStore(p => ({ id: p.entityId, name: p.serverData.name }));
    const { isTmOrderConfirmationRequired } = useDefaultValues(p => ({ isTmOrderConfirmationRequired: p.clientData.isTmOrderConfirmationRequired }));

    function addApiToken() {
        const current = useStore.getState().clientData.apiTokens ?? "";
        const token = Array.from(crypto.getRandomValues(new Uint8Array(16)))
            .map(b => b.toString(16).padStart(2, "0"))
            .join("");
        useStore.getState().setClientDataProp("apiTokens", current ? `${current}\n${token}` : token);
    }

    return <>
        <DialogTitle>{id == null ? `New call center` : `Call center ${name}`}</DialogTitle>
        {renderClose(props.close)}
        <DialogContent>
            <Grid container columnSpacing={2} alignItems="stretch">
                <Grid item xs={6}>{propTextField(useStore)("name", "Name")}</Grid>
                <Grid item xs={6}>{propBoolField(useStore)("isActive", "Active")}</Grid>
                <Grid item xs={6}>{propBoolField(useStore)("hideCherrypickData", "Hide order date and project for TM Agent")}</Grid>
                <Grid item xs={6}>{isTmOrderConfirmationRequired && propBoolField(useStore)("codForUnconfirmed", "COD for unconfirmed orders")}</Grid>
                <Grid item xs={12}>{propTextField(useStore)("apiTokens", "API tokens (line separated)", { multiline: true })}</Grid>
                <Grid item xs={12}>
                    <Typography variant="h6">Projects and products</Typography>
                    {projectInfos.map(p => <Project key={p.code + p.name} projectInfo={p} useStore={useStore} />)}
                </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <Button onClick={addApiToken}>Add API token</Button>
            <EntityStoreSaveButton useStore={useStore} />
            <Button onClick={props.close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>;
}

interface CallCenterProjectInfoDto {
    code: string;
    name: string;
    products: CallCenterProductInfoDto[];
    activeProductCount: number;
}

interface CallCenterProductInfoDto {
    projectId: number;
    code: string;
    name: string;
    isActive: boolean;
}

function Project({ projectInfo, useStore }: {
    projectInfo: CallCenterProjectInfoDto;
    useStore: UseEntityStore<CallCenterDto>;
}) {
    const landCode = useDefaultValues(p => p.clientData.landCode);
    const [isExpanded, setIsExpanded] = useState(false);
    const products = useStore(p => projectInfo.products.map(r => ({
        data: p.clientData.projects?.[r.projectId],
        info: r,
    })), (a, b) => a.length === b.length && a.every((p, i) => p.data === b[i].data));
    function toggleAllProducts(isActive: boolean) {
        const projects = { ...useStore.getState().clientData.projects };
        if (isActive) {
            products.forEach(p => projects[p.info.projectId] = {
                projectId: p.info.projectId, activeFrom: null, activeTo: null
            });
        } else {
            products.forEach(p => delete projects[p.info.projectId]);
        }
        useStore.getState().setClientDataProp("projects", projects);
    }
    const checkedCount = products.filter(p => p.data != null).length;
    const someChecked = checkedCount > 0 && checkedCount < products.length;
    const allChecked = checkedCount === products.length;
    const enabledProducts = products.filter(p => p.data != null);
    const enableProduct = enabledProducts[0]?.data;

    const firstProductHasData = enableProduct?.activeFrom != null ||
        enableProduct?.activeTo != null ||
        enableProduct?.fixPackagePrice != null ||
        enableProduct?.fixIndividualPrice != null ||
        enableProduct?.fixSmallPackagePrice != null ||
        enableProduct?.packageValuePercent != null ||
        enableProduct?.individualValuePercent != null ||
        enableProduct?.smallPackageValuePercent != null ||
        enableProduct?.fixArticlePrice != null ||
        enableProduct?.fixOrderPrice != null;

    const allSameData = enabledProducts.every(p => p.data!.activeFrom === enableProduct!.activeFrom &&
        p.data!.activeTo === enableProduct!.activeTo &&
        p.data!.fixPackagePrice === enableProduct!.fixPackagePrice &&
        p.data!.fixIndividualPrice === enableProduct!.fixIndividualPrice &&
        p.data!.fixSmallPackagePrice === enableProduct!.fixSmallPackagePrice &&
        p.data!.packageValuePercent === enableProduct!.packageValuePercent &&
        p.data!.individualValuePercent === enableProduct!.individualValuePercent &&
        p.data!.smallPackageValuePercent === enableProduct!.smallPackageValuePercent &&
        p.data!.fixArticlePrice === enableProduct!.fixArticlePrice &&
        p.data!.fixOrderPrice === enableProduct!.fixOrderPrice);

    const someDifferentData = enabledProducts.filter(p => p.data != null).some(p => p.data!.activeFrom !== enableProduct!.activeFrom ||
        p.data!.activeTo !== enableProduct!.activeTo ||
        p.data!.fixPackagePrice !== enableProduct!.fixPackagePrice ||
        p.data!.fixIndividualPrice !== enableProduct!.fixIndividualPrice ||
        p.data!.fixSmallPackagePrice !== enableProduct!.fixSmallPackagePrice ||
        p.data!.packageValuePercent !== enableProduct!.packageValuePercent ||
        p.data!.individualValuePercent !== enableProduct!.individualValuePercent ||
        p.data!.smallPackageValuePercent !== enableProduct!.smallPackageValuePercent ||
        p.data!.fixArticlePrice !== enableProduct!.fixArticlePrice ||
        p.data!.fixOrderPrice !== enableProduct!.fixOrderPrice
    );

    const allInactive = projectInfo.activeProductCount === 0;
    const [isHovered, setIsHovered] = useState(false);
    const additionalSettingsDialog = useDialog(close => <AdditionalSettingsDialog
        useCallCenterStore={useStore}
        data={firstProductHasData && allSameData ? enabledProducts[0].data : undefined}
        projectIds={enabledProducts.map(p => p.info.projectId)} close={close} />, "xs");
    return (allInactive && checkedCount === 0) ? null : <Grid item xs={12} container>
        {additionalSettingsDialog.render()}
        <Grid item xs={12} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
            <Checkbox icon={<ExpandLess />} checkedIcon={<ExpandMore />} checked={isExpanded}
                onChange={p => setIsExpanded(p.target.checked)} />
            <Checkbox checked={allChecked} indeterminate={someChecked} onChange={p => toggleAllProducts(p.target.checked)} />
            {projectInfo.code} {projectInfo.name} ({projectInfo.activeProductCount} active product{projectInfo.activeProductCount === 1 ? "" : "s"})
            {someDifferentData && " (individual projects data varies)"}
            {allSameData && firstProductHasData && getActiveFromToText(enabledProducts[0].data?.activeFrom, enabledProducts[0].data?.activeTo, landCode)}
            {checkedCount > 0 && isHovered && <IconButton title="More settings for all enabled products" onClick={() => additionalSettingsDialog.show()}><MoreVert /></IconButton>}
        </Grid>
        {isExpanded && products.map(p => <Product key={p.info.projectId} data={p.data} info={p.info} useStore={useStore} />)}
    </Grid>;
}

function Product({ useStore, data, info }: {
    useStore: UseEntityStore<CallCenterDto>;
    data: CallCenterProjectDto | undefined;
    info: CallCenterProductInfoDto;
}) {
    const landCode = useDefaultValues(p => p.clientData.landCode);
    function toggleProject(projectId: number, isActive: boolean) {
        const projects = { ...useStore.getState().clientData.projects };
        if (isActive) {
            projects[projectId] = { projectId, activeFrom: null, activeTo: null };
        } else {
            delete projects[projectId];
        }
        useStore.getState().setClientDataProp("projects", projects);
    }
    const additionalSettingsDialog = useDialog(close => <AdditionalSettingsDialog
        useCallCenterStore={useStore} data={data} projectIds={[info.projectId]} close={close} />, "xs");
    const [isHovered, setIsHovered] = useState(false);
    return <Grid key={info.projectId} item xs={12} ml={17} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
        {additionalSettingsDialog.render()}
        <Checkbox checked={data != null} onChange={p => toggleProject(info.projectId, p.target.checked)} />
        {info.code} {info.name} {info.isActive ? null : "(inactive)"}
        {getActiveFromToText(data?.activeFrom, data?.activeTo, landCode)}
        {data != null && isHovered && <IconButton title="More settings" onClick={() => additionalSettingsDialog.show()}><MoreVert /></IconButton>}
    </Grid>;
}

function getActiveFromToText(activeFrom: string | null | undefined, activeTo: string | null | undefined, landCode: LandCode) {
    const activeFromText = activeFrom != null ? "from " + formatDate(activeFrom, landCode) : null;
    const activeToText = activeTo != null ? "to " + formatDate(activeTo, landCode) : null;
    return activeFromText && activeToText ? ` (${activeFromText} ${activeToText})` :
        activeFromText ? ` (${activeFromText})` :
            activeToText ? ` (${activeToText})` :
                "";
}

interface AdditionalSettings {
    activeFrom: string | null;
    activeTo: string | null;
    activeFromFormatted?: string;
    activeToFormatted?: string;
    fixPackagePrice?: number;
    fixPackagePriceFormatted?: string;
    fixIndividualPrice?: number;
    fixIndividualPriceFormatted?: string;
    fixSmallPackagePrice?: number;
    fixSmallPackagePriceFormatted?: string;
    packageValuePercent?: number;
    packageValuePercentFormatted?: string;
    individualValuePercent?: number;
    individualValuePercentFormatted?: string;
    smallPackageValuePercent?: number;
    smallPackageValuePercentFormatted?: string;
    fixArticlePrice?: number;
    fixArticlePriceFormatted?: string;
    fixOrderPrice?: number;
    fixOrderPriceFormatted?: string;
}

export function AdditionalSettingsDialog({
    useCallCenterStore, data, projectIds, close
}: {
    useCallCenterStore: UseEntityStore<CallCenterDto>
    data: CallCenterProjectDto | undefined;
    projectIds: number[];
    close: () => void;
}) {
    const [useStore] = useState(() => createEntityStore<AdditionalSettings>({
        data: data ?? { activeFrom: null, activeTo: null },
        onSave(entity) {
            const projects = { ...useCallCenterStore.getState().clientData.projects };
            projectIds.forEach(id => {
                projects[id] = {
                    projectId: id, activeFrom: entity.activeFrom, activeTo: entity.activeTo, fixPackagePrice: entity.fixPackagePrice, fixIndividualPrice: entity.fixIndividualPrice,
                    packageValuePercent: entity.packageValuePercent, individualValuePercent: entity.individualValuePercent, fixSmallPackagePrice: entity.fixSmallPackagePrice, smallPackageValuePercent: entity.smallPackageValuePercent,
                    fixArticlePrice: entity.fixArticlePrice, fixOrderPrice: entity.fixOrderPrice,
                };
            });
            useCallCenterStore.getState().setClientDataProp("projects", projects);
            close();
        }, propValidators: [
            propValidValue("activeFrom", "activeFromFormatted"),
            propValidValue("activeTo", "activeToFormatted"),
            propValidValue("fixPackagePrice", "fixPackagePriceFormatted"),
            propValidValue("fixIndividualPrice", "fixIndividualPriceFormatted"),
            propValidValue("fixSmallPackagePrice", "fixSmallPackagePriceFormatted"),
            propValidValue("packageValuePercent", "packageValuePercentFormatted"),
            propValidValue("individualValuePercent", "individualValuePercentFormatted"),
            propValidValue("smallPackageValuePercent", "smallPackageValuePercentFormatted"),
            propValidValue("fixArticlePrice", "fixArticlePriceFormatted"),
            propValidValue("fixOrderPrice", "fixOrderPriceFormatted"),
            propValueValidator("fixPackagePrice", (p, e) => p != null && e.packageValuePercent != null && "Only fix or value in percent can be specified"),
            propValueValidator("fixSmallPackagePrice", (p, e) => p != null && e.smallPackageValuePercent != null && "Only fix or value in percent can be specified"),
            propValueValidator("fixIndividualPrice", (p, e) => p != null && e.individualValuePercent != null && "Only fix or value in percent can be specified"),
        ],
    }));
    return <>
        <DialogTitle>Additional settings</DialogTitle>
        {renderClose(close)}
        <DialogContent>
            <Grid container columnSpacing={2}>
                <Grid item xs={6}>{propDateField(useStore)("activeFrom", "activeFromFormatted", "Active from")}</Grid>
                <Grid item xs={6}>{propDateField(useStore)("activeTo", "activeToFormatted", "Active to")}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("fixPackagePrice", "fixPackagePriceFormatted", "Fix package price", { isCurrency: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("packageValuePercent", "packageValuePercentFormatted", "Package value in percent", { unit: "%" })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("fixSmallPackagePrice", "fixSmallPackagePriceFormatted", "Fix small package price", { isCurrency: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("smallPackageValuePercent", "smallPackageValuePercentFormatted", "Small package value in percent", { unit: "%" })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("fixIndividualPrice", "fixIndividualPriceFormatted", "Fix individual price", { isCurrency: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("individualValuePercent", "individualValuePercentFormatted", "Individual value in percent", { unit: "%" })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("fixArticlePrice", "fixArticlePriceFormatted", "Fix article price", { isCurrency: true })}</Grid>
                <Grid item xs={6}>{propNumberField(useStore)("fixOrderPrice", "fixOrderPriceFormatted", "Fix order price", { isCurrency: true })}</Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
            <EntityStoreSaveButton useStore={useStore} />
            <Button onClick={close} color="negative" variant="outlined">Close</Button>
        </DialogActions>
    </>
}
